import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { MessagesSquare } from 'lucide-react';
import microSimService from '../../services/microSimService';
import MicroSimGroupResults from './MicroSimGroupResults';

/**
 * "Micro simulations" block for the session report (trainer, client, super-admin).
 *
 * Self-contained so the session report only mounts it: it renders nothing when the
 * session had no micro simulation, or when the API refuses (feature off for this
 * trainer, no access) — the rest of the report is unaffected.
 */
const MicroSimSessionSection = ({ sessionId }) => {
  const [launches, setLaunches] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let alive = true;
    microSimService.history({ sessionId })
      .then((res) => {
        if (!alive) return;
        const list = res.launches || [];
        setLaunches(list);
        setSelected(list[0]?.id || null);
      })
      .catch(() => { /* hidden: not available to this viewer */ });
    return () => { alive = false; };
  }, [sessionId]);

  if (!launches.length || !selected) return null;

  return (
    <section className="rounded-2xl border border-neutral-200 bg-neutral-50/60 p-4 sm:p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-bold text-neutral-900">
          <MessagesSquare size={18} className="text-violet-600" /> Micro simulations
        </h2>
        {launches.length > 1 && (
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="rounded-lg border border-neutral-300 bg-white py-1.5 pl-2.5 pr-9 text-sm"
            aria-label="Choose a micro simulation"
          >
            {launches.map((l) => (
              <option key={l.id} value={l.id}>
                {l.title} · {new Date(l.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </option>
            ))}
          </select>
        )}
      </div>
      <MicroSimGroupResults key={selected} launchId={selected} />
    </section>
  );
};

MicroSimSessionSection.propTypes = { sessionId: PropTypes.string.isRequired };

export default MicroSimSessionSection;
